import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import { Container, FormFooter, FormInput, Navigation, NavBar } from '../../components';
import { COLOR, FONT } from '../../../constants/styles';
import { REQUESTING, SUCCESS } from '../../../actions/statusTypes';
import {
  resetCMRRevenueCycleStatus,
  updateCMRRevenueCycleService,
} from '../cmr_revenue_cycle/slice';

const MAX_ESTIMATED_SERVICE_VALUE = 9999999999.99;
const SAVE_SUCCESS_MESSAGE = 'CMR revenue cycle service saved.';

function isValidEstimatedServiceValue(value) {
  const trimmedValue = value?.trim();
  if (!trimmedValue) return false;

  const parts = trimmedValue.split('.');
  const hasValidDigits = (part) =>
    part.length > 0 && Array.from(part).every((character) => character >= '0' && character <= '9');
  const hasValidFormat =
    parts.length <= 2 &&
    hasValidDigits(parts[0]) &&
    (parts.length === 1 || (hasValidDigits(parts[1]) && parts[1].length <= 2));

  if (!hasValidFormat) return false;

  const numericValue = Number(trimmedValue);
  return Number.isFinite(numericValue) && numericValue <= MAX_ESTIMATED_SERVICE_VALUE;
}

const CMRRevenueCycleServiceForm = memo((props) => {
  useEffect(NavBar.setup(props), []);

  const dispatch = useDispatch();
  const employerId = props.employer?.employerId;
  const updateStatus = useSelector((state) => state.screens.cmrRevenueCycle.updateServiceStatus);
  const updateError = useSelector((state) => state.screens.cmrRevenueCycle.updateServiceError);
  const [estimatedServiceValue, setEstimatedServiceValue] = useState(
    String(props.item?.estimatedServiceValue ?? '')
  );

  useEffect(() => {
    dispatch(resetCMRRevenueCycleStatus());
    return () => {
      dispatch(resetCMRRevenueCycleStatus());
    };
  }, [dispatch]);

  useEffect(() => {
    setEstimatedServiceValue(String(props.item?.estimatedServiceValue ?? ''));
  }, [props.item]);

  const isSaving = updateStatus === REQUESTING;
  const validationMessage = useMemo(() => {
    if (!estimatedServiceValue.trim()) return 'Estimated Service Value is required.';
    if (!isValidEstimatedServiceValue(estimatedServiceValue)) {
      return 'Enter a non-negative USD amount with at most two decimal places.';
    }
    return null;
  }, [estimatedServiceValue]);

  const handleCancel = useCallback(() => {
    Navigation.close(props);
  }, [props]);

  const handleSave = useCallback(() => {
    if (!employerId || !props.item?.serviceId || validationMessage || !props.canEdit) return;

    dispatch(
      updateCMRRevenueCycleService({
        employerId,
        serviceId: props.item.serviceId,
        updates: { estimatedServiceValue: estimatedServiceValue.trim() },
      })
    );
  }, [
    dispatch,
    employerId,
    estimatedServiceValue,
    props.canEdit,
    props.item?.serviceId,
    validationMessage,
  ]);

  const footer = (
    <FormFooter
      onCancel={handleCancel}
      onSave={handleSave}
      cancelLabel={updateStatus === SUCCESS ? 'Done' : 'Cancel'}
      saveLabel={isSaving ? 'Saving…' : 'Save'}
      saveDisabled={
        isSaving || !!validationMessage || !employerId || !props.item?.serviceId || !props.canEdit
      }
    />
  );

  return (
    <Container
      scrollView={true}
      stickyFooter={footer}
      contentContainerStyle={styles.contentContainer}
      renderActivityOverlay={isSaving}
      successMessage={updateStatus === SUCCESS ? SAVE_SUCCESS_MESSAGE : null}
      errorMessage={updateError?.message || null}
    >
      <FormInput
        required={true}
        controlled={true}
        autoFocus={true}
        autoCapitalize="none"
        keyboardType="decimal-pad"
        label="Estimated Service Value (USD)"
        value={estimatedServiceValue}
        onChangeText={setEstimatedServiceValue}
      />
      {!!validationMessage && <Text style={styles.validationText}>{validationMessage}</Text>}
    </Container>
  );
});

const styles = StyleSheet.create({
  contentContainer: {
    padding: 20,
  },
  validationText: {
    ...FONT.regular,
    color: COLOR.error,
    marginTop: 10,
    alignSelf: 'flex-end',
  },
});

export default CMRRevenueCycleServiceForm;
